import React, { useEffect, useState, useContext, useRef } from "react";
import { BarChart } from '@mui/x-charts/BarChart';
import { ReviewsContext } from '../context/ReviewsContext';
import { BusinessContext } from '../context/BusinessContext';
import {
  CHART_MARGINS,
  MOBILE_CHART_MARGINS,
  CustomChartTooltip
} from './common/ChartDefaults';
import { useMediaQuery, useTheme } from '@mui/material';
import { processChartData, ChartStateWrapper } from './RatingChartUtils';

// Monthly Average Rating Chart component
const MonthlyAverageRatingChart = () => {
  const [monthlyData, setMonthlyData] = useState({ months: [], averages: [], yMin: 0, yMax: 5 });
  const [formattedMonths, setFormattedMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { reviewsData, view, setSelectedDate, setDateRange, selectedDate } = useContext(ReviewsContext);
  const { currencySymbol } = useContext(BusinessContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const chartContainerRef = useRef(null);

  // Format month from "MM/YYYY" to localized "Month YYYY"
  const formatMonth = (monthStr) => {
    const [month, year] = monthStr.split('/');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleString('default', { month: 'short', year: 'numeric' });
  };

  // Convert month string to start and end dates for the month
  const getMonthDateRange = (monthStr) => {
    const [month, year] = monthStr.split('/');
    const monthNum = parseInt(month) - 1; // JavaScript months are 0-based
    const startDate = new Date(parseInt(year), monthNum, 1); // First day of month
    
    // Last day of month (by going to first day of next month and subtracting 1 day)
    const endDate = new Date(parseInt(year), monthNum + 1, 0);
    
    // Set time to end of day for the end date
    endDate.setHours(23, 59, 59, 999);
    
    return { startDate, endDate };
  };

  // Handle bar click to filter reviews by month
  const onAxisClick = (event, params) => {
    // Ensure we have a valid data index
    if (params && params.dataIndex !== undefined) {
      // Get the month string from our original data (not the formatted display version)
      const monthStr = monthlyData.months[params.dataIndex];
      if (monthStr) {
        // Get start date for the clicked month
        const [month, year] = monthStr.split('/');
        const clickedDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        
        // Check if we're clicking the same month that's already selected
        // If so, clear the selection (toggle behavior)
        if (selectedDate && 
            selectedDate.getFullYear() === clickedDate.getFullYear() && 
            selectedDate.getMonth() === clickedDate.getMonth()) {
          setSelectedDate(null);
          console.log('Clearing month filter');
        } else {
          // Otherwise, set the selected date to filter reviews
          setSelectedDate(clickedDate);
          console.log(`Filtering to month: ${clickedDate.toLocaleDateString()}`);
        }
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    // Short timeout to ensure DOM is ready and container is properly sized
    const timeoutId = setTimeout(() => {
      if (reviewsData) {
        try {
          const reviews = (reviewsData || []).sort(
            (a, b) => new Date(a.estimated_datetime) - new Date(b.estimated_datetime)
          );
          
          if (reviews.length === 0) {
            setLoading(false);
            return;
          }
          
          const { monthlyData: newMonthlyData } = processChartData(reviews);
          if (newMonthlyData) {
            setMonthlyData(newMonthlyData);
            // Format the month labels for display
            setFormattedMonths(newMonthlyData.months.map(formatMonth));
          } else {
            setError("Could not process review data");
          }
        } catch (error) {
          console.error("Error processing review data for chart:", error);
          setError("Could not process review data");
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }, 100); // Small delay to ensure container is rendered
    
    return () => clearTimeout(timeoutId);
  }, [reviewsData]);

  const noData = !monthlyData || !monthlyData.months || monthlyData.months.length === 0;

  return (
    <ChartStateWrapper loading={loading} error={error} noData={noData}>
      <BarChart
        colors={['#1976d2']}
        sx={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          overflow: 'hidden',
          '& svg, & path, & g': {
            maxHeight: '350px !important'
          },
          '.MuiChartsAxis-label': {
            fill: '#546E7A',
            fontWeight: 500,
            fontSize: isSmallScreen ? 10 : 12
          },
          '.MuiChartsAxis-tickLabel': {
            fill: '#546E7A',
            fontSize: isSmallScreen ? 9 : 11
          },
          cursor: 'pointer' // Add pointer cursor to indicate clickable
        }}
        onAxisClick={onAxisClick}
        xAxis={[{
          data: formattedMonths,
          scaleType: 'band',
          tickLabelStyle: {
            angle: 45,
            textAnchor: 'start',
            fontSize: isSmallScreen ? 10 : 12,
          },
          tickLabelInterval: (_, i) => {
            // If we have 13 or fewer data points, show all labels
            if (formattedMonths.length <= 13) return true;
            
            // Otherwise, calculate the interval needed to show approximately 13 labels
            const interval = Math.ceil(formattedMonths.length / 13);
            
            // Show labels at calculated intervals
            return i % interval === 0;
          },
        }]}
        yAxis={[{
          valueFormatter: (value) => `${value.toFixed(1)}`,
          tickLabelStyle: {
            fontSize: isSmallScreen ? 9 : 11,
          },
          min: monthlyData.yMin,
          max: monthlyData.yMax,
          tickNumber: 3
        }]}
        series={[{
          data: monthlyData.averages,
          label: 'Avg. Rating',
          valueFormatter: (value) => `${value.toFixed(2)}★`,
        }]}
        slotProps={{
          legend: { hidden: true },
          tooltip: {
            content: (props) => <CustomChartTooltip {...props} currencySymbol={currencySymbol} view={view} />
          },
          svg: { 
            style: {
              maxHeight: '350px',
              overflow: 'hidden'
            }
          }
        }}
        margin={isSmallScreen ? 
          {...MOBILE_CHART_MARGINS, left: 40, right:40} : 
          CHART_MARGINS
        }
      />
    </ChartStateWrapper>
  );
};

export default MonthlyAverageRatingChart; 